import type { BranchSerialUnit } from './api';
import { normalizarSerie, problemaDeSerie } from './serie';

/**
 * Las reglas de «Equipos por serie» (docs/15 §2.41, segunda vuelta): qué se
 * le puede hacer a cada equipo según dónde está, y cómo se arma la lista de un
 * conteo por serie. Puras, para probarlas sin pantalla.
 */

export type EstadoDeEquipo = BranchSerialUnit['status'];

/** Los filtros de la lista, en el orden en que se buscan. */
export const FILTROS_DE_EQUIPOS: readonly { id: string; texto: string; estados: readonly EstadoDeEquipo[] }[] = [
  // «En la tienda» junta el estante, la vitrina y el taller: es lo que el libro
  // cuenta como stock, y lo que un conteo por serie espera encontrar.
  { id: 'tienda', texto: 'En la tienda', estados: ['in_stock', 'on_display', 'in_repair'] },
  { id: 'estante', texto: 'En el estante', estados: ['in_stock'] },
  { id: 'vitrina', texto: 'En vitrina', estados: ['on_display'] },
  { id: 'taller', texto: 'En el taller', estados: ['in_repair'] },
  { id: 'proveedor', texto: 'En el proveedor', estados: ['at_supplier'] },
  { id: 'devueltos', texto: 'Devueltos', estados: ['returned'] },
  { id: 'vendidos', texto: 'Vendidos', estados: ['sold'] },
  { id: 'baja', texto: 'De baja', estados: ['written_off'] },
  { id: 'todos', texto: 'Todos', estados: [] },
];

export type AccionDeEquipo =
  | 'a-vitrina'
  | 'al-taller'
  | 'al-estante'
  /** Del estante, la vitrina o el taller al proveedor: sale del libro como devolución. */
  | 'garantia-desde-tienda'
  /** El que devolvió un cliente dañado: ya salió del libro al venderse. */
  | 'garantia-devuelto'
  /** Vuelve del proveedor reparado o cambiado: entra a su lote, a su costo. */
  | 'volvio-del-proveedor';

/**
 * Lo que se le ofrece a un equipo. Solo lo que el servidor aceptaría: ofrecer
 * «al estante» a uno vendido es un botón que siempre contesta que no.
 *
 * `onShelf` falso con un estado de tienda es una serie en conflicto —su lote
 * ya no tiene la unidad—: moverla la niega el servidor, y se resuelve desde el
 * aviso del resumen.
 */
export function accionesDelEquipo(equipo: Pick<BranchSerialUnit, 'status' | 'onShelf'>): AccionDeEquipo[] {
  switch (equipo.status) {
    case 'in_stock':
      return equipo.onShelf ? ['a-vitrina', 'al-taller', 'garantia-desde-tienda'] : [];
    case 'on_display':
      return equipo.onShelf ? ['al-estante', 'al-taller', 'garantia-desde-tienda'] : [];
    case 'in_repair':
      // El taller de la tienda no pudo: va al proveedor desde ahí.
      return equipo.onShelf ? ['al-estante', 'garantia-desde-tienda'] : [];
    case 'returned':
      return ['garantia-devuelto'];
    case 'at_supplier':
      return ['volvio-del-proveedor'];
    case 'sold':
    case 'written_off':
      return [];
  }
}

export const TEXTO_DE_ACCION: Record<AccionDeEquipo, string> = {
  'a-vitrina': 'A vitrina',
  'al-taller': 'Al taller',
  'al-estante': 'Al estante',
  'garantia-desde-tienda': 'Al proveedor',
  'garantia-devuelto': 'Al proveedor',
  'volvio-del-proveedor': 'Volvió del proveedor',
};

/** El tono de la pastilla de cada estado: lo que no se puede vender, en aviso. */
export function tonoDeEstado(estado: EstadoDeEquipo): 'neutro' | 'exito' | 'aviso' | 'peligro' {
  switch (estado) {
    case 'in_stock':
      return 'exito';
    case 'on_display':
    case 'in_repair':
    case 'at_supplier':
    case 'returned':
      return 'aviso';
    case 'written_off':
      return 'peligro';
    case 'sold':
      return 'neutro';
  }
}

/**
 * Agrega una lectura al conteo por serie. La misma caja leída dos veces cuenta
 * una; un IMEI con el último dígito mal se dice al leerlo y no al comparar,
 * donde saldría «sobra» y el equipo de verdad, «falta».
 */
export function agregarAlConteo(
  leidas: readonly string[],
  texto: string,
): { series: string[]; problema: string | null } {
  const serie = normalizarSerie(texto);
  if (serie === '') return { series: [...leidas], problema: null };

  const problema = problemaDeSerie(serie);
  if (problema !== null) return { series: [...leidas], problema: `«${serie}»: ${problema}` };
  if (leidas.includes(serie)) {
    return { series: [...leidas], problema: `La serie ${serie} ya está leída en este conteo.` };
  }
  return { series: [...leidas, serie], problema: null };
}

/**
 * El segundo número de un equipo (IMEI 2 o serie de fábrica), como viaja: nulo
 * si se dejó vacío. El mismo que el primero no es un segundo número.
 */
export function segundoNumero(primero: string, texto: string): { valor: string | null; problema: string | null } {
  const segundo = normalizarSerie(texto);
  if (segundo === '') return { valor: null, problema: null };
  const problema = problemaDeSerie(segundo);
  if (problema !== null) return { valor: null, problema: `IMEI 2: ${problema}` };
  if (segundo === normalizarSerie(primero)) {
    return { valor: null, problema: 'El IMEI 2 es el mismo que el primero.' };
  }
  return { valor: segundo, problema: null };
}
